import {
  faChevronsLeft,
  faChevronsRight,
  faRotate,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { Col, Row, Spinner } from 'reactstrap';

import { Button } from '../../../../_components/button';
import { useToastr } from '../../../../_components/toastr';
import { useSteps } from '../steps/use-steps';

function NextButton({ isSubmitting }: { isSubmitting: boolean }) {
  const formContext = useFormContext();
  const navigate = useNavigate();
  const { currentStep, nextStep } = useSteps();
  const keys = useMemo(
    () => Object.keys(currentStep?.schema.fields || {}),
    [currentStep]
  );
  const isDisabled = Object.keys(formContext?.formState?.errors || {}).some(
    (f) => keys.includes(f)
  );

  return nextStep ? (
    <Button
      aria-description="Press Enter to move to the next page"
      aria-label={`${currentStep.title} Next`}
      color="primary"
      id="next-45fad180-9ab0-44a6-afb4-4cb5cafdd9a7"
      disabled={isDisabled}
      onClick={async (e: { preventDefault: () => void }) => {
        e.preventDefault();
        if (currentStep) {
          const { schema, onExit } = currentStep;
          let isValid = true;
          if (schema && formContext.trigger) {
            isValid = await formContext.trigger(keys);
          }
          let result = {
            success: isValid,
          };
          if (onExit) {
            result = await onExit(isValid);
          }
          if (result.success) {
            navigate(nextStep.path);
          } else {
            // jump out of process
          }
        }
      }}
    >
      Next
      <FontAwesomeIcon icon={faChevronsRight} className="ms-2" />
    </Button>
  ) : (
    <Button
      aria-description="Press Enter to submit the form"
      type="submit"
      color="success"
      id="submit-e5b4d7cb-f8be-43a5-b273-a6736c3dcb8f"
      style={{ minWidth: '100px' }}
      disabled={isSubmitting}
      aria-label={`${currentStep.title}: Submit`}
    >
      Submit
      {isSubmitting ? (
        <Spinner size="sm" className="ms-2" />
      ) : (
        <FontAwesomeIcon className="ms-2" icon={faCheck} size="1x" />
      )}
    </Button>
  );
}

export default function WizardNavigator({
  isSubmitting,
}: {
  isSubmitting: boolean;
}) {
  const toastr = useToastr();

  const { steps, previousStep, currentStepIndex, currentStep } = useSteps();
  const navigate = useNavigate();
  const { clearErrors, reset } = useFormContext();

  // TODO CCRA-37242
  useEffect(() => {
    if (currentStepIndex !== 0 && !currentStep) {
      return navigate(steps[0].path);
    }
  }, [currentStepIndex, currentStep]);

  return (
    <fieldset>
      <Row className="d-flex justify-content-between">
        <Col className="text-center">
          {previousStep && (
            <Button
              aria-description="Press Enter to move to the previous page"
              aria-label={`${currentStep.title} Previous`}
              color="primary"
              id="previous-6851758d-e382-4970-ac4d-995cc66caefa"
              onClick={() => {
                clearErrors();
                navigate(previousStep.path);
              }}
            >
              <FontAwesomeIcon icon={faChevronsLeft} className="me-2" />
              Previous
            </Button>
          )}
        </Col>
        <Col className="text-center">
          <Button
            aria-label={`${currentStep.title} Start Over`}
            aria-description="Press Enter to clear the form and begin again"
            color="primary"
            id="start-over-36ce54cc-66e1-45c2-b085-5ff26e6e026c"
            onClick={async (e: { preventDefault: () => void }) => {
              e.preventDefault();
              reset();
              navigate(steps[0].path);
              toastr.success(
                <span>All fields are cleared, please start over.</span>
              );
            }}
          >
            Start Over
            <FontAwesomeIcon icon={faRotate} className="ms-2" />
          </Button>
        </Col>
        <Col className="text-center">
          <NextButton isSubmitting={isSubmitting} />
        </Col>
      </Row>
    </fieldset>
  );
}
